import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

import ContactDirectoryHeader from "@/app/pages/contactDirectory/ContactDirectoryHeader";
import ContactCrawlLocation from "@/app/pages/contactIndustrySearch/ContactCrawlLocation";
import SearchResult from "@/components/SearchAndResultShared/SearchResult"
import PaginationElementComponent from "@/components/PaginationElementComponent";
import Loader from "@/components/loader";
import HooversAdBlock from "@/app/pages/companyProfile/hooversAdBlock";
import ContactCrawlRelatedIndustries from "@/app/pages/contactIndustrySearch/ContactCrawlRelatedIndustries";
import ContactCrawlHeader from "@/app/pages/contactIndustrySearch/ContactCrawlHeader";

import { ContactIndustryLocationGroupAndUrl } from "@/app/pages/contactDirectory/interface";
import { dnbComLog, isNotUndefined, isNotUndefinedOrNull, LogSeverity } from "@/shared/FunctionCore";
import { getContactIndustrySearchData, getContactIndustryLocationData } from "@/services/contactDirectory";
import "@/app/pages/contactIndustrySearch/styles/ContactCrawlComponentsStyles.scss";
import { setDigitalData } from "@/services/setDigitalData";
import Overlay from "@/components/overlay";
import { usePageURL } from "@/shared/customHooks";

const ContactIndustrySearch = () => {
  const { pathname, state } = useLocation();
  const [resourcePath, setResourcePath] = useState<string>("");
  const [contactIndustryCrawlData, setContactIndustryCrawlData] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [headerAndRelatedIndustriesData, setHeaderAndRelatedIndustriesData] = useState();
  const [showLocationFailure, setShowLocationFailure] = useState(false);
  const [locationGroups, setLocationGroups] = useState<ContactIndustryLocationGroupAndUrl[]>([]);
  const [locationMetaInformation, setLocationMetaInformation] = useState(null);
  const {countryCode} = usePageURL()

  setDigitalData({ gcpPageName: 'contactDirectorySearch' });

  const extractAddressDataFromURL = () => {
    const values = resourcePath.split(".")
    const notNeededIndexes = [0, values.length - 1]
    const neededDataArr = values.filter((k, i) => !notNeededIndexes.includes(i))

    let addressData = {
      addressCountry: "US"
    }

    neededDataArr.forEach((value, i) => {
      if (i === 2) {
        addressData = {
          ...addressData,
          addressRegion: neededDataArr[i]
        }
      }
      if (i === 3) {
        addressData = {
          ...addressData,
          addressLocality: neededDataArr[i]
        }
      }
    })

    return addressData
  }

  const fetchContactsData = () => {
    if (resourcePath) {
      const path = resourcePath.split(".")[1]
      setIsLoading(true);

      try {
        const params = {
          ...extractAddressDataFromURL(),
          naics: state?.naics,
          pageNumber,
          resourcePath: path
        }

        getContactIndustrySearchData(params)
          .then((res) => {
            if (res && res.data !== null) {
              setContactIndustryCrawlData(res.data);
              setIsLoading(false);
            }
          })
          .catch((error) => {
            setIsLoading(false);
            dnbComLog(LogSeverity.ERROR, error);
          });
      } catch {
        setIsLoading(false);
      }
    }
  };

  // let timeoutId: number | undefined = void 0;

  const fetchLocationData = () => {
    if (resourcePath) {
      const path = resourcePath.split(".")[1]
      setIsLocationLoading(true);
      setShowLocationFailure(false)

      try {
        const params = {
          ...extractAddressDataFromURL(),
          naics: state?.naics,
          pageNumber: 1,
          resourcePath: path
        }

        getContactIndustryLocationData(params)
          .then((ax) => {
            const { data } = ax;

            if (isNotUndefined(data)) {
              setLocationGroups(data?.contactIndustryLocationGroups)
              setLocationMetaInformation(data?.contactIndustryHeaderDataInner)
              setHeaderAndRelatedIndustriesData(data?.breadCrumbRelatedIndustries)

              if (isNotUndefinedOrNull(data?.contactIndustryLocationGroups)) {
                if (showLocationFailure) setShowLocationFailure(false)
              } else setShowLocationFailure(true)
            }

            setIsLocationLoading(false);

            // if (isNotUndefined(timeoutId)) {
            //   window.clearTimeout(timeoutId);
            // }
          })
          .catch((error) => {
            setIsLocationLoading(false);
            dnbComLog(LogSeverity.ERROR, error);
          });
      } catch {
        setIsLocationLoading(false);
      }
    }
  }

  // useEffect(() => {
  //   timeoutId = window.setTimeout(() => {
  //     setShowLocationFailure(true);
  //   }, 20_000);

  //   return () => {
  //     if (isNotUndefined(timeoutId)) {
  //       window.clearTimeout(timeoutId);
  //     }
  //   };
  // }, []);

  useEffect(() => {
    fetchLocationData()
  }, [resourcePath])

  useEffect(() => {
    fetchContactsData()
  }, [resourcePath, pageNumber])

  useEffect(() => {
    let path = pathname.substr(pathname.indexOf("/") + 1);
    const splittedPath = path.split("/")

    if (isNotUndefined(splittedPath[1])) {
      const encodedQS = encodeURI(splittedPath[1])
      splittedPath[1] = encodedQS
      splittedPath.splice(0, 1)
      setResourcePath(splittedPath.splice(0, 1)[0])
    }
  }, [pathname])

  const isMobileWidth = false;

  const renderPagination = () => (
    <PaginationElementComponent
      currentPage={pageNumber}
      currentPageValueClassName="currentPageValue"
      ellipsesClassName="ellipses"
      ltgtClassName="ltgt"
      nextButtonClassName="nextButton"
      nextClassName="next"
      onPageChange={(nu) => {
        setPageNumber(nu);
        // setScrollToSearchResultsContainer(true);
      }}
      pageValueButtonClassName="pageValueButton"
      paginationContainerRef={void 0}
      previousButtonClassName="previousButton"
      previousClassName="previous"
      siblings={isMobileWidth ? 3 : 5}
      totalPages={contactIndustryCrawlData?.totalPages ?? void 0}
      wrapperClassName="paginationWrapper"
    />
  );

  const hideLocationComponent = resourcePath && resourcePath.split(".").length === 6;

  return (
    <>
      <div className="ContactDirectoryHeader">
        <ContactDirectoryHeader />
        <ContactCrawlHeader
          contactIndustryHeaderDataInner={locationMetaInformation}
          totalContacts={contactIndustryCrawlData?.totalContacts}
          contactIndustryBreadcrumbData={headerAndRelatedIndustriesData?.breadcrumbList}
        />
      </div>
      {isLoading ? <Loader inProgress={isLoading} /> : (
        contactIndustryCrawlData &&
        <SearchResult
          resultData={contactIndustryCrawlData}
          paginationComponent={renderPagination()}
          forComponent="contactDirectoryIndustry"
          inputSearchCombineString="No data found"
          tableClass="crawlDataTable"
        />
      )}
      {!hideLocationComponent &&
        <ContactCrawlLocation
          locationGroups={locationGroups}
          locationMetaInformation={locationMetaInformation}
          showLocationFailure={showLocationFailure}
          isLoading={isLocationLoading}
        />
      }
      <ContactCrawlRelatedIndustries
        relatedIndustries={headerAndRelatedIndustriesData?.relatedIndustriesMap}
      />
      <HooversAdBlock forPage="contactProfile" />
      <Overlay
        countryCode={countryCode}
        isEmailDisplay={false}
        isPhoneDisplay={false}
      />
    </>
  )
}

export default ContactIndustrySearch